import { useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSystemStatusStore } from '@/stores/systemStatusStore';
import { useUserStore } from '@/stores/userStore';
import { Button } from '../ui/Button';
import { AlertTriangle, Shield } from 'lucide-react';

interface SystemGateProps {
  children: ReactNode;
}

export const SystemGate: React.FC<SystemGateProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const status = useSystemStatusStore((state) => state.status);
  const isLoading = useSystemStatusStore((state) => state.isLoading);
  const error = useSystemStatusStore((state) => state.error);
  const loadStatus = useSystemStatusStore((state) => state.loadStatus);
  const currentUser = useUserStore((state) => state.currentUser);

  useEffect(() => {
    if (!status && !isLoading) {
      loadStatus();
    }
  }, [status, isLoading, loadStatus]);

  const handleRetry = () => {
    loadStatus();
  };

  const openAdminSettings = () => {
    navigate('/admin/settings');
  };

  if (isLoading && !status) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center gap-3 bg-background">
        <div className="text-sm text-muted-foreground">Checking AI configuration…</div>
      </div>
    );
  }

  if (error && !status) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background px-4">
        <div className="max-w-lg w-full border border-border rounded-lg bg-card p-6 space-y-4 text-center">
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button onClick={handleRetry}>Retry status check</Button>
        </div>
      </div>
    );
  }

  if (status && status.requiresSetup) {
    const isAdmin = currentUser?.role === 'admin';
    const isAdminSettingsRoute = location.pathname.startsWith('/admin/settings');
    const allowAdminSettings = isAdmin && isAdminSettingsRoute;

    if (allowAdminSettings) {
      return (
        <>
          <div className="fixed top-0 left-0 right-0 z-50 bg-amber-900/90 text-amber-50 border-b border-amber-700 shadow-md">
            <div className="max-w-6xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3">
              <AlertTriangle className="w-4 h-4" />
              <p className="text-sm flex-1">
                {status.message ||
                  'Configure at least one AI provider to unlock the workspace. Save changes below, then click “Retry status check”.'}
              </p>
              <Button variant="outline" size="sm" onClick={handleRetry} className="text-amber-50 border-amber-400">
                I updated the settings
              </Button>
            </div>
          </div>
          <div className="pt-16">{children}</div>
        </>
      );
    }

    return (
      <div className="fixed inset-0 flex items-center justify-center px-4 bg-background">
        <div className="w-full max-w-xl border border-border rounded-lg bg-card shadow-lg p-6 space-y-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            <div>
              <h2 className="text-lg font-semibold">AI configuration required</h2>
              <p className="text-sm text-muted-foreground">
                {status.message || 'This workspace needs at least one AI provider with a valid API key.'}
              </p>
            </div>
          </div>

          <div className="rounded-md border border-border bg-background/40 p-4 space-y-2">
            <p className="text-sm text-muted-foreground">
              Active provider: <span className="font-medium text-foreground capitalize">{status.activeProvider}</span>
            </p>
            <p className="text-sm text-muted-foreground">
              OpenAI key configured: <span className="font-medium">{status.providers.openai ? 'Yes' : 'No'}</span>
            </p>
            <p className="text-sm text-muted-foreground">
              Anthropic key configured:{' '}
              <span className="font-medium">{status.providers.anthropic ? 'Yes' : 'No'}</span>
            </p>
            {status.missingReasons.length > 0 && (
              <ul className="list-disc list-inside text-sm text-muted-foreground">
                {status.missingReasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            )}
          </div>

          {isAdmin ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Shield className="w-4 h-4" />
                You are signed in as an admin. Configure API keys to continue.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={openAdminSettings}>Open Admin Settings</Button>
                <Button variant="outline" onClick={handleRetry}>
                  I updated the settings
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Ask an administrator to configure the AI provider keys. Once updated, click retry.
              </p>
              <Button variant="outline" onClick={handleRetry}>
                Retry status check
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
